import { createTheme, PaletteMode } from '@mui/material';

// CaosBlitz Theme - Cyberpunk Command Center Aesthetic
export const getCaosBlitzTheme = (mode: PaletteMode = 'dark') =>
  createTheme({
    ...(mode === 'dark'
      ? {}
      : {
          // Improve default light-mode typography contrast (MUI defaults are ok,
          // but our component overrides lean "dark UI" unless we nudge this).
          typography: {
            // Keep font families from below; only adjust baseline color contrast via palette.
          },
        }),
    palette: {
      mode,
      ...(mode === 'dark'
        ? {}
        : {
            // Ensure MUI derives sensible "action" colors for light mode.
            // (Defaults are fine, but our UI benefits from slightly clearer hover/selected contrast.)
            action: {
              hover: 'rgba(2, 6, 23, 0.04)',
              selected: 'rgba(8, 145, 178, 0.12)',
              disabledBackground: 'rgba(2, 6, 23, 0.08)',
              focus: 'rgba(8, 145, 178, 0.10)',
            },
          }),
      primary: {
        main: mode === 'dark' ? '#00f0ff' : '#0891b2', // Electric Cyan / More muted cyan
        light: mode === 'dark' ? '#4df7ff' : '#06b6d4',
        dark: mode === 'dark' ? '#00b8c4' : '#0e7490',
        contrastText: '#ffffff',
      },
      secondary: {
        main: mode === 'dark' ? '#e91e63' : '#db2777', // Pink - less vibrant in light
        light: mode === 'dark' ? '#ff4d9e' : '#f472b6',
        dark: mode === 'dark' ? '#c4004e' : '#be185d',
        contrastText: '#ffffff',
      },
      success: {
        main: mode === 'dark' ? '#10b981' : '#059669', // More muted green in light
        light: mode === 'dark' ? '#4dffaa' : '#34d399',
        dark: mode === 'dark' ? '#00c466' : '#047857',
      },
      warning: {
        main: mode === 'dark' ? '#f59e0b' : '#d97706', // More muted amber in light
        light: mode === 'dark' ? '#ffd54f' : '#fbbf24',
        dark: mode === 'dark' ? '#c79100' : '#b45309',
      },
      error: {
        main: mode === 'dark' ? '#ef4444' : '#dc2626', // More muted red in light
        light: mode === 'dark' ? '#ff6690' : '#f87171',
        dark: mode === 'dark' ? '#c4004e' : '#b91c1c',
      },
      info: {
        main: mode === 'dark' ? '#0ea5e9' : '#0284c7', // More muted blue in light
        light: mode === 'dark' ? '#4dc8e3' : '#38bdf8',
        dark: mode === 'dark' ? '#0087a6' : '#0369a1',
      },
      background: {
        default: mode === 'dark' ? '#0a0e17' : '#f6f8fb',
        paper: mode === 'dark' ? 'rgba(26, 35, 50, 0.78)' : 'rgba(255, 255, 255, 0.92)',
      },
      text: {
        primary: mode === 'dark' ? '#f0f4f8' : '#0b1220',
        secondary: mode === 'dark' ? '#8b9cb3' : '#475569',
        disabled: mode === 'dark' ? '#4a5568' : '#94a3b8',
      },
      divider: mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(2, 6, 23, 0.12)',
    },
    typography: {
      fontFamily: '"DM Sans", "Helvetica", "Arial", sans-serif',
      h1: {
        fontFamily: '"JetBrains Mono", monospace',
        fontWeight: 700,
        letterSpacing: '-0.02em',
      },
      h2: {
        fontFamily: '"JetBrains Mono", monospace',
        fontWeight: 600,
        letterSpacing: '-0.01em',
      },
      h3: {
        fontFamily: '"JetBrains Mono", monospace',
        fontWeight: 600,
      },
      h4: {
        fontFamily: '"JetBrains Mono", monospace',
        fontWeight: 600,
      },
      h5: {
        fontFamily: '"JetBrains Mono", monospace',
        fontWeight: 500,
      },
      h6: {
        fontFamily: '"JetBrains Mono", monospace',
        fontWeight: 500,
      },
      button: {
        fontWeight: 600,
        letterSpacing: '0.02em',
      },
      body1: {
        fontSize: '1rem',
        lineHeight: 1.6,
      },
      body2: {
        fontSize: '0.875rem',
        lineHeight: 1.5,
      },
    },
    shape: {
      borderRadius: 12,
    },
    shadows: [
      'none',
      '0 2px 8px rgba(0, 0, 0, 0.15)',
      '0 4px 16px rgba(0, 0, 0, 0.2)',
      '0 8px 24px rgba(0, 0, 0, 0.25)',
      '0 12px 32px rgba(0, 0, 0, 0.3)',
      '0 0 20px rgba(0, 240, 255, 0.3)', // Cyan glow
      '0 0 30px rgba(0, 240, 255, 0.4)',
      '0 0 40px rgba(0, 240, 255, 0.5)',
      '0 0 20px rgba(255, 0, 110, 0.3)', // Pink glow
      '0 0 30px rgba(255, 0, 110, 0.4)',
      '0 0 40px rgba(255, 0, 110, 0.5)',
      '0 0 20px rgba(0, 255, 136, 0.3)', // Green glow
      '0 0 30px rgba(0, 255, 136, 0.4)',
      '0 0 40px rgba(0, 255, 136, 0.5)',
      '0 0 20px rgba(255, 195, 7, 0.3)', // Yellow glow
      '0 0 30px rgba(255, 195, 7, 0.4)',
      '0 0 40px rgba(255, 195, 7, 0.5)',
      '0 0 20px rgba(255, 51, 102, 0.3)', // Red glow
      '0 0 30px rgba(255, 51, 102, 0.4)',
      '0 0 40px rgba(255, 51, 102, 0.5)',
      '0 0 20px rgba(0, 180, 216, 0.3)', // Blue glow
      '0 0 30px rgba(0, 180, 216, 0.4)',
      '0 0 40px rgba(0, 180, 216, 0.5)',
      '0 0 60px rgba(0, 240, 255, 0.6)', // Strong cyan glow
      '0 0 80px rgba(0, 240, 255, 0.7)',
    ],
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: mode === 'dark' ? '#0a0e17' : '#f6f8fb',
            backgroundImage: `
              radial-gradient(ellipse at 20% 30%, ${
                mode === 'dark' ? 'rgba(0, 240, 255, 0.03)' : 'rgba(8, 145, 178, 0.10)'
              } 0%, transparent 50%),
              radial-gradient(ellipse at 80% 70%, ${
                mode === 'dark' ? 'rgba(255, 0, 110, 0.02)' : 'rgba(219, 39, 119, 0.06)'
              } 0%, transparent 50%),
              linear-gradient(${
                mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(2, 6, 23, 0.04)'
              } 1px, transparent 1px),
              linear-gradient(90deg, ${
                mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(2, 6, 23, 0.04)'
              } 1px, transparent 1px)
            `,
            backgroundSize: '100%, 100%, 50px 50px, 50px 50px',
            backgroundAttachment: 'fixed',
          },
          '*': {
            scrollbarWidth: 'thin',
            scrollbarColor: mode === 'dark' ? '#00f0ff #1a2332' : '#0891b2 #e5e7eb',
          },
          '*::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '*::-webkit-scrollbar-track': {
            background: mode === 'dark' ? '#1a2332' : '#e5e7eb',
          },
          '*::-webkit-scrollbar-thumb': {
            background: mode === 'dark' ? '#00f0ff' : '#0891b2',
            borderRadius: '4px',
          },
          '*::-webkit-scrollbar-thumb:hover': {
            background: mode === 'dark' ? '#4df7ff' : '#06b6d4',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundImage:
              mode === 'dark'
                ? 'linear-gradient(135deg, rgba(0, 240, 255, 0.03) 0%, transparent 50%)'
                : 'linear-gradient(135deg, rgba(8, 145, 178, 0.06) 0%, transparent 50%)',
            backdropFilter: 'blur(12px)',
            border:
              mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.06)' : '1px solid rgba(2, 6, 23, 0.10)',
            borderRadius: 16,
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '2px',
              background: `linear-gradient(90deg, ${mode === 'dark' ? '#00f0ff' : '#0891b2'} 0%, transparent 50%)`,
            },
            '&:hover': {
              borderColor: mode === 'dark' ? 'rgba(0, 240, 255, 0.3)' : 'rgba(8, 145, 178, 0.25)',
              boxShadow: mode === 'dark' ? '0 0 20px rgba(0, 240, 255, 0.1)' : '0 8px 24px rgba(2, 6, 23, 0.08)',
              transform: 'translateY(-2px)',
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage:
              mode === 'dark'
                ? 'linear-gradient(135deg, rgba(0, 240, 255, 0.02) 0%, transparent 50%)'
                : 'linear-gradient(135deg, rgba(8, 145, 178, 0.05) 0%, transparent 50%)',
            backdropFilter: 'blur(12px)',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            textTransform: 'none',
            fontWeight: 600,
            padding: '10px 24px',
            transition: 'all 0.2s ease',
            '&:active': {
              transform: 'scale(0.98)',
            },
          },
          containedPrimary: {
            background: mode === 'dark'
              ? 'linear-gradient(135deg, #00f0ff 0%, #00b8c4 100%)'
              : 'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)',
            boxShadow: mode === 'dark' 
              ? '0 0 15px rgba(0, 240, 255, 0.15)' 
              : '0 2px 8px rgba(0, 0, 0, 0.1)',
            '&:hover': {
              background: mode === 'dark'
                ? 'linear-gradient(135deg, #4df7ff 0%, #00f0ff 100%)'
                : 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
              boxShadow: mode === 'dark' 
                ? '0 0 20px rgba(0, 240, 255, 0.25)' 
                : '0 4px 12px rgba(0, 0, 0, 0.15)',
              transform: 'scale(1.02)',
            },
          },
          containedSecondary: {
            background: mode === 'dark'
              ? 'linear-gradient(135deg, #e91e63 0%, #c4004e 100%)'
              : 'linear-gradient(135deg, #db2777 0%, #be185d 100%)',
            boxShadow: mode === 'dark'
              ? '0 0 15px rgba(233, 30, 99, 0.15)'
              : '0 2px 8px rgba(0, 0, 0, 0.1)',
            '&:hover': {
              background: mode === 'dark'
                ? 'linear-gradient(135deg, #ff4d9e 0%, #e91e63 100%)'
                : 'linear-gradient(135deg, #f472b6 0%, #db2777 100%)',
              boxShadow: mode === 'dark'
                ? '0 0 20px rgba(233, 30, 99, 0.25)'
                : '0 4px 12px rgba(0, 0, 0, 0.15)',
              transform: 'scale(1.02)',
            },
          },
          outlined: {
            borderWidth: '2px',
            '&:hover': {
              borderWidth: '2px',
              backgroundColor: mode === 'dark'
                ? 'rgba(0, 240, 255, 0.08)'
                : 'rgba(8, 145, 178, 0.08)',
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            transition: 'all 0.2s ease',
            '&:hover': {
              transform: 'scale(1.05)',
              backgroundColor: mode === 'dark' 
                ? 'rgba(0, 240, 255, 0.08)' 
                : 'rgba(8, 145, 178, 0.06)',
            },
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            height: 8,
            borderRadius: 4,
            backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(2, 6, 23, 0.10)',
          },
          bar: {
            borderRadius: 4,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontWeight: 500,
          },
          filled: {
            // Override filled chips to use darker backgrounds in light mode for better contrast
            '&.MuiChip-colorPrimary': {
              backgroundColor: mode === 'dark' 
                ? undefined // Use default in dark mode
                : '#0e7490', // Darker cyan in light mode
              color: '#ffffff',
            },
            '&.MuiChip-colorSuccess': {
              backgroundColor: mode === 'dark' 
                ? undefined
                : '#047857', // Darker green in light mode
              color: '#ffffff',
            },
            '&.MuiChip-colorWarning': {
              backgroundColor: mode === 'dark' 
                ? undefined
                : '#b45309', // Darker amber in light mode
              color: '#ffffff',
            },
            '&.MuiChip-colorError': {
              backgroundColor: mode === 'dark' 
                ? undefined
                : '#b91c1c', // Darker red in light mode
              color: '#ffffff',
            },
            '&.MuiChip-colorInfo': {
              backgroundColor: mode === 'dark' 
                ? undefined
                : '#0369a1', // Darker blue in light mode
              color: '#ffffff',
            },
          },
          outlined: {
            borderWidth: '2px',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundImage: 'linear-gradient(180deg, rgba(0, 240, 255, 0.02) 0%, transparent 50%)',
            backdropFilter: 'blur(20px)',
            borderRight: '1px solid rgba(0, 240, 255, 0.1)',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundImage: 'linear-gradient(135deg, rgba(0, 240, 255, 0.03) 0%, transparent 50%)',
            backdropFilter: 'blur(20px)',
            borderBottom: '1px solid rgba(0, 240, 255, 0.1)',
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            margin: '4px 8px',
            transition: 'all 0.2s ease',
            '&.Mui-selected': {
              backgroundColor: mode === 'dark' ? 'rgba(0, 240, 255, 0.15)' : 'rgba(8, 145, 178, 0.14)',
              borderLeft: `3px solid ${mode === 'dark' ? '#00f0ff' : '#0891b2'}`,
              '&:hover': {
                backgroundColor: mode === 'dark' ? 'rgba(0, 240, 255, 0.2)' : 'rgba(8, 145, 178, 0.18)',
              },
            },
            '&:hover': {
              backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(2, 6, 23, 0.04)',
            },
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            backdropFilter: 'blur(8px)',
          },
          standardSuccess: {
            backgroundColor: mode === 'dark' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(16, 185, 129, 0.12)',
            border: mode === 'dark' ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid rgba(16, 185, 129, 0.25)',
            color: mode === 'dark' ? '#f0f4f8' : '#0b1220',
          },
          standardError: {
            backgroundColor: mode === 'dark' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.10)',
            border: mode === 'dark' ? '1px solid rgba(239, 68, 68, 0.3)' : '1px solid rgba(239, 68, 68, 0.22)',
            color: mode === 'dark' ? '#f0f4f8' : '#0b1220',
          },
          standardWarning: {
            backgroundColor: mode === 'dark' ? 'rgba(245, 158, 11, 0.15)' : 'rgba(245, 158, 11, 0.12)',
            border: mode === 'dark' ? '1px solid rgba(245, 158, 11, 0.3)' : '1px solid rgba(245, 158, 11, 0.25)',
            color: mode === 'dark' ? '#f0f4f8' : '#0b1220',
          },
          standardInfo: {
            backgroundColor: mode === 'dark' ? 'rgba(14, 165, 233, 0.15)' : 'rgba(2, 132, 199, 0.10)',
            border: mode === 'dark' ? '1px solid rgba(14, 165, 233, 0.3)' : '1px solid rgba(2, 132, 199, 0.22)',
            color: mode === 'dark' ? '#f0f4f8' : '#0b1220',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(2, 6, 23, 0.20)',
                borderWidth: '2px',
              },
              '&:hover fieldset': {
                borderColor: mode === 'dark' ? 'rgba(0, 240, 255, 0.4)' : 'rgba(8, 145, 178, 0.45)',
              },
              '&.Mui-focused fieldset': {
                borderColor: mode === 'dark' ? '#00f0ff' : '#0891b2',
                boxShadow: mode === 'dark' ? '0 0 10px rgba(0, 240, 255, 0.15)' : '0 0 0 3px rgba(8, 145, 178, 0.18)',
              },
            },
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(2, 6, 23, 0.20)',
            color: mode === 'dark' ? '#8b9cb3' : '#334155',
            '&.Mui-selected': {
              backgroundColor: mode === 'dark' ? 'rgba(0, 240, 255, 0.15)' : 'rgba(8, 145, 178, 0.14)',
              color: mode === 'dark' ? '#00f0ff' : '#0891b2',
              borderColor: mode === 'dark' ? '#00f0ff' : '#0891b2',
              '&:hover': {
                backgroundColor: mode === 'dark' ? 'rgba(0, 240, 255, 0.2)' : 'rgba(8, 145, 178, 0.18)',
              },
            },
          },
        },
      },
      MuiSkeleton: {
        styleOverrides: {
          root: {
            backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(2, 6, 23, 0.08)',
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            backgroundColor: mode === 'dark' 
              ? '#00f0ff' 
              : '#0891b2',
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(2, 6, 23, 0.70)',
            '&.Mui-selected': {
              color: mode === 'dark' ? '#00f0ff' : '#0891b2',
            },
            '&:hover': {
              color: mode === 'dark' ? 'rgba(0, 240, 255, 0.8)' : 'rgba(8, 145, 178, 0.90)',
            },
          },
        },
      },
      MuiBadge: {
        styleOverrides: {
          colorPrimary: {
            backgroundColor: mode === 'dark' 
              ? undefined // Use default in dark mode
              : '#0e7490', // Darker cyan in light mode
          },
          colorSuccess: {
            backgroundColor: mode === 'dark' 
              ? undefined
              : '#047857',
          },
          colorWarning: {
            backgroundColor: mode === 'dark' 
              ? undefined
              : '#b45309',
          },
          colorError: {
            backgroundColor: mode === 'dark' 
              ? undefined
              : '#b91c1c',
          },
          colorInfo: {
            backgroundColor: mode === 'dark' 
              ? undefined
              : '#0369a1',
          },
        },
      },
    },
  });

export default getCaosBlitzTheme;

